import React, { useCallback, useRef, useState } from 'react';
import { Upload, FileSpreadsheet, X, AlertCircle, ArrowRight, Loader2, Info, CheckCircle2, Download } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const ALLOWED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB

// Per-category schema the user sees before uploading. Keep in sync with
// agent/models.py::CATEGORY_SCHEMAS on the backend.
const CATEGORY_INSTRUCTIONS = {
  freight: {
    title: 'Freight shipment columns (Cat 4 / Cat 9)',
    required: [
      ['Origin', 'pickup city or address'],
      ['Destination', 'delivery city or address'],
      ['Weight', 'shipment weight (kg, tonnes, or lbs)'],
      ['Transport Mode', 'road, rail, air, or sea'],
      ['Vehicle Type', 'e.g. truck, van, freight train'],
    ],
    optional: 'Fuel Type, Goods Name, Cost, GHG Category. Column names don\'t need to match exactly — AI will auto-detect.',
  },
  business_travel: {
    title: 'Business travel columns (Cat 6)',
    required: [
      ['Mode', 'flight / rail / car / bus / taxi / ferry / hotel / motorbike'],
      ['Either Distance + Unit…', 'numeric distance (km / passenger.km / nights / hours)'],
      ['…or Origin + Destination', 'the backend auto-calculates distance via Google Maps'],
    ],
    optional: 'Vehicle Type, Cabin Class (flights), Fuel Type (cars), Size Class, Radiative Forcing, Passengers, Region (hotels), Travel Date, Purpose, Employee Ref. Names don\'t need to match — AI auto-detects.',
  },
  employee_commute: {
    title: 'Employee commute columns (Cat 7)',
    required: [
      ['Mode', 'car / motorbike / bus / rail / taxi / ferry'],
      ['Days per week', 'how many days the employee commutes'],
      ['Weeks in period', 'e.g. 48 per year'],
      ['Either One-way distance + Unit…', 'numeric distance per leg'],
      ['…or Home Address + Work Address', 'the backend geocodes to auto-calculate one-way distance'],
    ],
    optional: 'Employee ID, Vehicle Type, Size Class, Fuel Type, WFH Days per Week. Column names are matched semantically.',
  },
};

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001'}/api`;

async function downloadTemplate(activityType) {
  const token = localStorage.getItem('token');
  const url = `${API}/agent/template/${activityType}`;
  try {
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `carbonroute-${activityType}-template.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch (err) {
    console.error('Template download failed', err);
  }
}

export default function FileDropZone({ onFileSelected, isUploading, activityType = 'freight' }) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const inputRef = useRef(null);

  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `Invalid format "${ext}". Please upload .xlsx, .xls, or .csv`;
    }
    if (file.size > MAX_SIZE) {
      return `File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum is 10MB`;
    }
    return null;
  };

  const handleFile = useCallback((file) => {
    setError(null);
    const err = validateFile(file);
    if (err) {
      setError(err);
      return;
    }
    setSelectedFile(file);
  }, []);

  const handleNext = useCallback(() => {
    if (selectedFile) {
      onFileSelected(selectedFile);
    }
  }, [selectedFile, onFileSelected]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const onDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const onDragLeave = () => setDragActive(false);

  const clearFile = () => {
    setSelectedFile(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <Card className="pro-card">
      <CardContent className="p-6">
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => !isUploading && !selectedFile && inputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-xl p-12 text-center transition-all duration-200
            ${selectedFile ? 'border-green-300 bg-green-50/30' : dragActive ? 'border-sky-500 bg-sky-50' : 'border-gray-300 hover:border-sky-400 hover:bg-gray-50'}
            ${!selectedFile && !isUploading ? 'cursor-pointer' : ''}
            ${isUploading ? 'opacity-60 cursor-not-allowed' : ''}
          `}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            disabled={isUploading}
          />

          {selectedFile ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                <FileSpreadsheet className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-800">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
              {!isUploading && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); clearFile(); }}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X className="w-4 h-4 mr-1" /> Remove
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-sky-100 flex items-center justify-center">
                <Upload className="w-7 h-7 text-sky-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-800">
                  Drop your spreadsheet here or click to browse
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Supports .xlsx, .xls, .csv — up to 10MB, 5000 rows max
                </p>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-3 flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {selectedFile && (
          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleNext}
              disabled={isUploading}
              className="bg-sky-600 hover:bg-sky-700 text-white px-6"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Category-aware instructions */}
        <div className="mt-5 border border-sky-100 bg-sky-50/50 rounded-xl p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-sky-600 flex-shrink-0" />
              <h4 className="text-sm font-semibold text-gray-700">
                {CATEGORY_INSTRUCTIONS[activityType]?.title || 'Required Columns in Your Spreadsheet'}
              </h4>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate(activityType)}
              className="text-xs"
            >
              <Download className="w-3 h-3 mr-1" />
              Download sample CSV
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {(CATEGORY_INSTRUCTIONS[activityType]?.required || []).map(([name, desc]) => (
              <div key={name} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-600">
                  <span className="font-medium text-gray-700">{name}</span> — {desc}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-sky-100">
            <p className="text-xs text-gray-500">
              <span className="font-medium">Optional:</span> {CATEGORY_INSTRUCTIONS[activityType]?.optional}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
